import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import GlassCard from '../components/GlassCard';
import { familiesAPI, peopleAPI, relationshipsAPI } from '../services/api';
import { parseDateComponents, getMonthNameShort, getMonthNameFull, getMonth, getDay, getYear } from '../utils/dateUtils';

const FamilyDetailPage = () => {
  const { id } = useParams();
  const { theme } = useTheme();
  const [family, setFamily] = useState(null);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [editingCode, setEditingCode] = useState(false);
  const [familyCode, setFamilyCode] = useState('');
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newMember, setNewMember] = useState({
    first_name: '',
    last_name: '',
    preferred_name: '',
    date_of_birth: '',
    sex: 'unknown',
    relationship_type: '', // 'parent', 'child', 'spouse', 'sibling', or ''
    related_person_id: '', // ID of existing person to link to
    relationship_role: '', // 'mom', 'dad', 'son', 'daughter', 'wife', 'husband', 'sibling'
  });

  useEffect(() => {
    loadFamily();
    loadPeople();
  }, [id]);

  const loadFamily = async () => {
    try {
      const response = await familiesAPI.get(id);
      setFamily(response.data);
      setFamilyName(response.data.name);
      setFamilyCode(response.data.join_code || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load family');
    }
  };

  const handleUpdateFamilyName = async () => {
    if (!familyName.trim()) return;
    try {
      const response = await familiesAPI.update(id, { name: familyName });
      setFamily(response.data);
      setEditingName(false);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update family name');
    }
  };

  const handleUpdateFamilyCode = async () => {
    if (familyCode.trim() && familyCode.trim().length !== 8) {
      setError('Join code must be exactly 8 characters');
      return;
    }
    try {
      const response = await familiesAPI.update(id, { join_code: familyCode.trim() || null });
      setFamily(response.data);
      setFamilyCode(response.data.join_code || '');
      setEditingCode(false);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update join code');
    }
  };

  const copyJoinLink = () => {
    const joinLink = `${window.location.origin}/join/${family.join_code}`;
    navigator.clipboard.writeText(joinLink).then(() => {
      alert('Join link copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy link. Please copy manually: ' + joinLink);
    });
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!newMember.first_name.trim()) {
      setError('First name is required');
      return;
    }
    try {
      // Create the person first
      const personResponse = await peopleAPI.create(id, {
        first_name: newMember.first_name,
        last_name: newMember.last_name,
        preferred_name: newMember.preferred_name,
        date_of_birth: newMember.date_of_birth || null,
        sex: newMember.sex || 'unknown',
      });
      const newPersonId = personResponse.data.id;

      // If a relationship is specified, create it
      if (newMember.relationship_type && newMember.related_person_id) {
        if (newMember.relationship_type === 'parent-child') {
          // Parent-child relationship
          if (newMember.relationship_role === 'mom' || newMember.relationship_role === 'dad') {
            // New person is parent, existing person is child
            await relationshipsAPI.parentChild.create(id, {
              parent_person_id: newPersonId,
              child_person_id: newMember.related_person_id,
              kind: 'biological',
            });
          } else if (newMember.relationship_role === 'son' || newMember.relationship_role === 'daughter') {
            // New person is child, existing person is parent
            await relationshipsAPI.parentChild.create(id, {
              parent_person_id: newMember.related_person_id,
              child_person_id: newPersonId,
              kind: 'biological',
            });
          }
        } else if (newMember.relationship_type === 'partnership') {
          // Partnership (spouse/wife/husband)
          await relationshipsAPI.partnerships.create(id, {
            person_id_1: newPersonId,
            person_id_2: newMember.related_person_id,
            partnership_type: 'spouse',
          });
        }
      }

      await loadPeople();
      setShowAddMemberForm(false);
      setNewMember({ 
        first_name: '', 
        last_name: '', 
        preferred_name: '', 
        date_of_birth: '',
        sex: 'unknown',
        relationship_type: '',
        related_person_id: '',
        relationship_role: '',
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add member');
    }
  };

  const loadPeople = async () => {
    try {
      setLoading(true);
      const response = await peopleAPI.list(id);
      setPeople(response.data.people || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load people');
    } finally {
      setLoading(false);
    }
  };

  // Filter people based on search term
  const filteredPeople = people.filter((person) => {
    if (!searchTerm.trim()) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search by name (first, last, preferred)
    const fullName = `${person.first_name || ''} ${person.last_name || ''} ${person.preferred_name || ''}`.toLowerCase();
    if (fullName.includes(searchLower)) return true;
    
    // Search by location (city, country)
    const location = `${person.city || ''} ${person.country || ''}`.toLowerCase();
    if (location.includes(searchLower)) return true;
    
    // Search by month and day of birth (e.g., "Aug 22", "8/22", "08-22")
    if (person.date_of_birth) {
      const components = parseDateComponents(person.date_of_birth);
      if (components) {
        const month = components.month; // 1-12
        const day = components.day; // 1-31
        const monthName = getMonthNameShort(person.date_of_birth); // "Aug"
        const monthNameFull = getMonthNameFull(person.date_of_birth); // "August"
        
        // Check various date formats
        if (
          `${month}/${day}`.includes(searchLower) ||
          `${month}-${day}`.includes(searchLower) ||
          `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`.includes(searchLower) ||
          `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`.includes(searchLower) ||
          `${monthName} ${day}`.toLowerCase().includes(searchLower) ||
          `${monthNameFull} ${day}`.toLowerCase().includes(searchLower) ||
          `${day} ${monthName}`.toLowerCase().includes(searchLower) ||
          `${day} ${monthNameFull}`.toLowerCase().includes(searchLower)
        ) {
          return true;
        }
      }
    }
    
    return false;
  });

  if (loading && !family) {
    return (
      <GlassCard style={{ textAlign: 'center', padding: '48px' }}>
        <p style={{ color: theme.text.secondary }}>Loading...</p>
      </GlassCard>
    );
  }

  return (
    <div>
      <Link
        to="/families"
        style={{
          color: theme.text.secondary,
          textDecoration: 'none',
          marginBottom: '24px',
          display: 'inline-block',
        }}
      >
        ← Back to Families
      </Link>

      {family && (
        <>
          <GlassCard style={{ marginBottom: '24px' }}>
            {editingName ? (
              <div>
                <input
                  type="text"
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '24px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                  }}
                  autoFocus
                />
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={handleUpdateFamilyName}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      background: theme.accent.primary,
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingName(false);
                      setFamilyName(family.name);
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: theme.glass.border,
                      background: 'transparent',
                      color: theme.text.primary,
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h1 style={{ fontSize: '36px', marginBottom: '8px', color: theme.text.primary }}>
                    {family.name}
                  </h1>
                  <p style={{ color: theme.text.muted }}>
                    Created: {new Date(family.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => setEditingName(true)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: theme.glass.border,
                    background: 'transparent',
                    color: theme.text.primary,
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  ✏️ Edit
                </button>
              </div>
            )}
          </GlassCard>

          {/* Join Code Section */}
          <GlassCard style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '20px', marginBottom: '16px', color: theme.text.primary }}>
              Family Join Code
            </h3>
            {editingCode ? (
              <div>
                <input
                  type="text"
                  value={familyCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
                    setFamilyCode(value);
                  }}
                  placeholder="Enter 8-character code (leave empty to auto-generate)"
                  maxLength={8}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: theme.glass.border,
                    background: theme.card.background,
                    color: theme.text.primary,
                    fontSize: '18px',
                    fontFamily: 'monospace',
                    letterSpacing: '2px',
                    marginBottom: '8px',
                  }}
                  autoFocus
                />
                <p style={{ color: theme.text.muted, fontSize: '12px', marginBottom: '12px' }}>
                  Leave empty to auto-generate a unique code
                </p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={handleUpdateFamilyCode}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      background: theme.accent.primary,
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingCode(false);
                      setFamilyCode(family.join_code || '');
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: theme.glass.border,
                      background: 'transparent',
                      color: theme.text.primary,
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {family.join_code ? (
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      marginBottom: '12px',
                    }}>
                      <span style={{
                        fontSize: '24px',
                        fontFamily: 'monospace',
                        letterSpacing: '3px',
                        color: theme.accent.primary,
                        fontWeight: 'bold',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        background: theme.card.background,
                        border: `2px solid ${theme.accent.primary}`,
                      }}>
                        {family.join_code}
                      </span>
                      <button
                        onClick={copyJoinLink}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: theme.glass.border,
                          background: theme.accent.primary,
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        📋 Copy Join Link
                      </button>
                    </div>
                    <p style={{ color: theme.text.muted, fontSize: '14px', marginBottom: '8px' }}>
                      Share this link with family members: <br />
                      <code style={{
                        background: theme.card.background,
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        wordBreak: 'break-all',
                      }}>
                        {window.location.origin}/join/{family.join_code}
                      </code>
                    </p>
                    <button
                      onClick={() => setEditingCode(true)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: theme.glass.border,
                        background: 'transparent',
                        color: theme.text.primary,
                        fontSize: '12px',
                        cursor: 'pointer',
                      }}
                    >
                      ✏️ Change Code
                    </button>
                  </div>
                ) : (
                  <div>
                    <p style={{ color: theme.text.muted, marginBottom: '12px' }}>
                      No join code set. Create one to allow easy family access.
                    </p>
                    <button
                      onClick={() => setEditingCode(true)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: 'none',
                        background: theme.accent.primary,
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Generate Join Code
                    </button>
                  </div>
                )}
              </div>
            )}
          </GlassCard>
        </>
      )}

      {error && (
        <GlassCard style={{ marginBottom: '24px', background: 'rgba(255, 0, 0, 0.1)' }}>
          <p style={{ color: theme.text.primary }}>Error: {error}</p>
        </GlassCard>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '24px', color: theme.text.primary }}>
          Family Members ({people.length})
        </h2>
        <button
          onClick={() => setShowAddMemberForm(!showAddMemberForm)}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: theme.glass.border,
            background: theme.accent.primary,
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + Add Member
        </button>
      </div>

      {/* Search Bar */}
      {people.length > 0 && (
        <GlassCard style={{ marginBottom: '24px' }}>
          <input
            type="text"
            placeholder="Search by name, location, or birth date (e.g., Aug 22, 8/22)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '8px',
              border: theme.glass.border,
              background: theme.card.background,
              color: theme.text.primary,
              fontSize: '16px',
            }}
          />
        </GlassCard>
      )}

      {showAddMemberForm && (
        <GlassCard style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '20px', marginBottom: '16px', color: theme.text.primary }}>Add New Member</h3>
          <form onSubmit={handleAddMember}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <input
                type="text"
                placeholder="First Name *"
                value={newMember.first_name}
                onChange={(e) => setNewMember({ ...newMember, first_name: e.target.value })}
                required
                style={{
                  padding: '10px',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.card.background,
                  color: theme.text.primary,
                  fontSize: '14px',
                }}
              />
              <input
                type="text"
                placeholder="Last Name"
                value={newMember.last_name}
                onChange={(e) => setNewMember({ ...newMember, last_name: e.target.value })}
                style={{
                  padding: '10px',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.card.background,
                  color: theme.text.primary,
                  fontSize: '14px',
                }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <input
                type="text"
                placeholder="Preferred Name"
                value={newMember.preferred_name}
                onChange={(e) => setNewMember({ ...newMember, preferred_name: e.target.value })}
                style={{
                  padding: '10px',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.card.background,
                  color: theme.text.primary,
                  fontSize: '14px',
                }}
              />
              <input
                type="date"
                placeholder="Date of Birth"
                value={newMember.date_of_birth}
                onChange={(e) => setNewMember({ ...newMember, date_of_birth: e.target.value })}
                style={{
                  padding: '10px',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.card.background,
                  color: theme.text.primary,
                  fontSize: '14px',
                }}
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: theme.text.secondary,
                  fontSize: '14px',
                }}
              >
                Gender
              </label>
              <select
                value={newMember.sex}
                onChange={(e) => setNewMember({ ...newMember, sex: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.card.background,
                  color: theme.text.primary,
                  fontSize: '14px',
                }}
              >
                <option value="unknown">Unknown</option>
                <option value="male">Male ♂</option>
                <option value="female">Female ♀</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  background: theme.accent.primary,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Add Member
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddMemberForm(false);
                  setNewMember({ first_name: '', last_name: '', preferred_name: '', date_of_birth: '', sex: 'unknown' });
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: 'transparent',
                  color: theme.text.primary,
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </GlassCard>
      )}

      {people.length === 0 ? (
        <GlassCard style={{ textAlign: 'center', padding: '48px' }}>
          <p style={{ color: theme.text.secondary }}>No members in this family yet. Add your first member!</p>
        </GlassCard>
      ) : filteredPeople.length === 0 ? (
        <GlassCard style={{ textAlign: 'center', padding: '48px' }}>
          <p style={{ color: theme.text.secondary }}>No members found matching your search.</p>
        </GlassCard>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '24px',
          }}
        >
          {filteredPeople.map((person) => {
            const genderColor = person.sex === 'male' ? 'rgba(37, 99, 235, 0.2)' : person.sex === 'female' ? 'rgba(234, 88, 12, 0.2)' : 'rgba(107, 114, 128, 0.2)';
            const borderColor = person.sex === 'male' ? 'rgba(37, 99, 235, 0.3)' : person.sex === 'female' ? 'rgba(234, 88, 12, 0.3)' : 'rgba(107, 114, 128, 0.3)';
            return (
              <Link
                key={person.id}
                to={`/people/${person.id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <GlassCard
                  style={{
                    background: genderColor,
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  {person.picture_url && (
                    <img
                      src={person.picture_url}
                      alt={`${person.first_name} ${person.last_name || ''}`}
                      style={{
                        width: '100%',
                        height: '200px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        marginBottom: '12px',
                      }}
                    />
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '16px', color: theme.text.primary }}>
                      {person.sex === 'male' ? '♂' : person.sex === 'female' ? '♀' : '⚥'}
                    </span>
                    <h3 style={{ fontSize: '16px', margin: 0, color: theme.text.primary, fontWeight: 600 }}>
                      {person.preferred_name || person.first_name} {person.last_name || ''}
                    </h3>
                  </div>
                  {person.date_of_birth && (
                    <p style={{ color: theme.text.muted, fontSize: '12px', margin: 0 }}>
                      {getYear(person.date_of_birth)}
                    </p>
                  )}
                </GlassCard>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FamilyDetailPage;

